import { useEffect, useState } from "react";

import {
    ListContainer,
    ListContext,
    PAGE_DEFAULT,
    PAGE_SIZE_UNLIMITED,
    Page,
    PageRoute,
    SearchBar,
    buildTemplatePath,
} from "@compass/core";

import { ListFooter } from "components";

import { ListFilterBase, Permission, CohortListDto, CohortListResource } from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { CohortListPageHeader } from "./CohortListPageHeader";
import { CohortTable } from "./CohortTable";

const routeName = "cohorts";

export const cohortListPageRoute: PageRoute = {
    handle: "Cohorts",
    templatePath: buildTemplatePath(routeName),
    getPath: () => buildTemplatePath(routeName),
};

export const CohortListPage = () => {
    const ROOT_PERMISSION = Permission.MatchUtility;

    const [listItems, setListItems] = useState<CohortListDto[]>([]);
    const { listFilter, setListFilter } = useInitListFilter<ListFilterBase>({
        sortObjects: [{ property: "name", isDesc: false }],
    });

    const [total, setTotal] = useState<number>();
    const [resource, setResource] = useState<CohortListResource | null>(null);
    const [searchFocused, setSearchFocused] = useState<boolean>(false);

    const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
        listFilter,
        setListFilter,
        fetchList: async (listFilter) => {
            const data = await ApiService.cohorts.getAll();
            setListItems(data || []);
        },
        fetchTotal: async (listFilter) => {
            const data = await ApiService.cohorts.getAll();
            setTotal(data?.length || 0);
        },
    });

    useEffect(() => {
        loadList(listFilter);
        ApiService.cohorts.getListResource()
            .then(setResource)
            .catch((err) => {
                console.error("Failed to fetch list resource", err);
            });
    }, []);

    return (
        <Page header={<CohortListPageHeader rootPermission={ROOT_PERMISSION} handle={cohortListPageRoute.handle} />}>
            <ListContainer
                fullHeight
                header={<SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />}
                tableContainer={
                    resource && (
                        <CohortTable
                            rootPermission={ROOT_PERMISSION}
                            tableContainerRef={tableContainerRef}
                            cohorts={listItems}
                            setCohorts={setListItems} 
                            resource={resource}
                            searchFocused={searchFocused}
                            sortObjects={listFilter.sortObjects}
                            sort={applySort}
                            loadList={() => loadList(listFilter)}
                        />
                    )
                }
                footer={
                    <ListFooter
                        total={total}
                        size={listFilter.pageSize}
                        page={listFilter.page}
                        onPageChange={applyPage}
                        onSizeChange={applyPageSize}
                        onSortMethodChange={applySortMethod}
                    />
                }
            />
            <ListContext.Provider
                value={{
                    loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
                }}
            >
                <Outlet />
            </ListContext.Provider>
        </Page>
    );
};
